import React, { useState, useEffect, useRef } from 'react';
import { GoogleGenerativeAI } from "@google/generative-ai";
import ReactMarkdown from 'react-markdown';
import { animate, svg, utils, stagger, onScroll, createDrawable, createScope, createTimeline } from 'animejs';
import './chatmodal.css';

const ChatModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'model', text: 'Hi there! 👋\nI’m Pixie. Ask anything about Clarissa!' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const path1Ref = useRef(null);
  const path2Ref = useRef(null);
  const toggleRef = useRef(null);
  const windowRef = useRef(null);

  const toggleChat = () => setIsOpen(!isOpen);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  // Button & Tail move animation
  useEffect(() => {
    if (toggleRef.current) {
      const scope = createScope({
        mediaQueries: {
          isSmall: '(max-width: 480px)',
          isLarge: '(min-width: 481px)',
          reduceMotion: '(prefers-reduced-motion)',
        }
      })
        .add(self => {
          const { isSmall, reduceMotion } = self.matches;

          if (isOpen) {
            // Animate button
            animate(toggleRef.current, {
              x: isSmall ? -(window.innerWidth / 2 - 80) : -150,
              y: 5,   // Positive value creates a clear gap
              rotate: 360,
              scale: 1.1,
              duration: reduceMotion ? 0 : 800,
              ease: 'outElastic(1, .6)'
            });

            // Animate tail if window exists
            if (windowRef.current) {
              animate(windowRef.current, {
                '--tail-offset': isSmall ? 'calc(50% - 10px)' : '180px',
                duration: 800,
                ease: 'outElastic(1, .6)'
              });
            }
          } else {
            animate(toggleRef.current, {
              x: 0,
              y: 0,
              rotate: 0,
              scale: 1,
              duration: 500,
              ease: 'outQuad'
            });
          }
        });

      return () => scope.revert();
    }
  }, [isOpen]);

  // Morphing button animation
  useEffect(() => {
    if (path1Ref.current && path2Ref.current) {
      const $path1 = path1Ref.current;
      const $path2 = path2Ref.current;

      const generatePoints = () => {
        const total = utils.random(4, 64);
        const r1 = utils.random(4, 56);
        const r2 = 56;
        const isOdd = n => n % 2;
        let points = '';
        for (let i = 0, l = isOdd(total) ? total + 1 : total; i < l; i++) {
          const r = isOdd(i) ? r1 : r2;
          const a = (2 * Math.PI * i / l) - Math.PI / 2;
          const x = 152 + utils.round(r * Math.cos(a), 0);
          const y = 56 + utils.round(r * Math.sin(a), 0);
          points += `${x},${y} `;
        }
        return points;
      };

      let currentAnimation;
      const animateRandomPoints = () => {
        utils.set($path2, { points: generatePoints() });
        currentAnimation = animate($path1, {
          points: svg.morphTo($path2),
          ease: 'inOutCirc',
          duration: 500,
          onComplete: animateRandomPoints
        });
      };

      animateRandomPoints();

      return () => {
        if (currentAnimation) currentAnimation.pause();
      };
    }
  }, []);

  // AI Thinking animation
  useEffect(() => {
    if (isLoading) {
      const scope = createScope({
        mediaQueries: {
          isSmall: '(max-width: 400px)',
        }
      })
        .add(self => {
          self.addOnce(() => {
            createTimeline().add('.thinking-container .circle', {
              backgroundColor: [
                $el => utils.get($el, `--hex-red-1`) || '#c8f21d',
                $el => utils.get($el, `--hex-citrus-1`) || '#20b69e',
              ],
              loop: true,
              alternate: true,
              duration: 2000,
            }, stagger(150));
          });

          self.keepTime(() => createTimeline().add('.thinking-container .circle', {
            x: self.matches.isSmall ? [-5, 5] : [-15, 15],
            scale: [0.8, 1.2],
            loop: true,
            alternate: true,
          }, stagger(150)).init());
        });

      return () => scope.revert();
    }
  }, [isLoading]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', text: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
      if (!apiKey) {
        throw new Error("API Key not found");
      }

      const genAI = new GoogleGenerativeAI(apiKey);

      const systemInstruction = `
        You are Pixie, the personal AI assistant for Clarissa B. Vega.
        Your goal is to professionally and warmly answer questions about Clarissa's background, skills, and projects.
        
        Here is Clarissa's Resume / Portfolio Context:
        Name: Clarissa B. Vega
        Role: Software Developer
        Focus: Exploring AI & Blockchain Technologies
        Location: Philippines
        Social Links:
        - GitHub: https://github.com/clangv
        - LinkedIn: https://www.linkedin.com/in/clarissa-vega-10600521a/
        Technical Skills: Python, JavaScript, C#, C++, SQL, React, Node.js, Tailwind, AI/NLP.
        Bio: Passionate developer exploring AI and Blockchain.
        Tone: Friendly, professional, helpful.
      `;

      const model = genAI.getGenerativeModel({
        model: "gemini-2.5-flash-lite",
        systemInstruction: systemInstruction
      });

      const history = messages
        .filter((msg, index) => index > 0 || msg.role === 'user')
        .map(m => ({
          role: m.role === 'model' ? 'model' : 'user',
          parts: [{ text: m.text }]
        }));

      const chat = model.startChat({ history });
      const result = await chat.sendMessage(input);
      const response = await result.response;
      setMessages(prev => [...prev, { role: 'model', text: response.text() }]);
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prev => [...prev, { role: 'model', text: "Sorry, I encountered an error. Please check your API key." }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="chat-widget">
      <button
        ref={toggleRef}
        className={`chat-toggle ${isOpen ? 'open' : ''}`}
        onClick={toggleChat}
        aria-label="Toggle chat"
      >
        {isOpen && <span className="close-icon"></span>}
        <svg className="chat-morph-svg" viewBox="0 0 300 112" width="90" height="50">
          <polygon ref={path1Ref} points="152,56 152,56" fill="var(--chat--color-primary)" />
          <polygon ref={path2Ref} points="152,56 152,56" fill="none" style={{ visibility: 'hidden' }} />
        </svg>
      </button>

      {isOpen && (
        <div ref={windowRef} className="chat-window">
          <div className="chat-header">
            <div className="chat-header-left">
              <div className="assistant-icon-box" i>
                <img src="/assets/imgs/baymax.gif" alt="Baymax" className="assistant-gif" />
              </div>
              <div className="chat-header-title">Koramax</div>
            </div>
            <button className="chat-close" onClick={toggleChat}>✕</button>
          </div>

          <div className="chat-messages">
            {messages.map((msg, index) => (
              <div key={index} className={`chat-message ${msg.role}`}>
                <div className="message-content">
                  {msg.role === 'model' ? (
                    <ReactMarkdown
                      components={{
                        a: ({ node, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer" className="chat-link" />
                        )
                      }}
                    >
                      {msg.text}
                    </ReactMarkdown>
                  ) : (
                    msg.text
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="chat-message model">
                <div className="message-content thinking-container">
                  <div className="circle"></div>
                  <div className="circle"></div>
                  <div className="circle"></div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {!isLoading && messages.length > 0 && messages[messages.length - 1].role === 'model' && (
            <div className="chat-suggestion-area">
              <button className="suggestion-btn" onClick={() => setInput("Tell me more about your experience")}>
                Tell me more about your experience
              </button>
              <button className="suggestion-btn" onClick={() => setInput("What are your top technical skills?")}>
                What are your top technical skills?
              </button>
            </div>
          )}

          <form className="chat-input-area" onSubmit={handleSend}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me something..."
              disabled={isLoading}
            />
            <button type="submit" disabled={isLoading || !input.trim()}>➤</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatModal;
